<template>
  <div>
    <transition name="fade-nl">
      <form
        method="post"
        @submit.prevent="authenticateWithPin"
        v-if="usePin"
      >
        <div class="mb-3">
          <div
            class="mb-1 text-center"
            v-html="$t('sentence.received_code_email', {email: anonymousEmail})"
          />
          <div class="mb-3 text-center text-muted">
            <small>
              <em>{{ $t('sentence.mail_in_spam') }}</em>
            </small>
          </div>

          <div class="row">
            <div class="mb-2" :class="[smallScreen ? 'col-12': 'col-lg-6']">
              <v-button
                text="phrase.different_email"
                size="xs"
                style-type="link"
                icon="undo"
                :block="true"
                @click="resetRegistration()"
              />
            </div>
            <div
              class="d-flex align-items-center justify-content-center"
              :class="[smallScreen ? 'col-12': 'col-lg-6']"
            >
              <v-button
                text="phrase.send_new_code"
                size="xs"
                style-type="link"
                icon="paper-plane"
                :block="true"
                @click="resetPin()"
                :loader="loader"
                v-if="!resendPin"
              />
              <transition name="fade">
                <div v-if="resendPin">
                  <i class="fa fa-paper-plane"></i> {{ $t('phrase.new_code_sent') }}
                </div>
              </transition>
            </div>
          </div>

          <hr />

          <input
            required="required"
            class="form-control form-control-lg text-big text-center"
            id="register-pin-code"
            ref="pinCode"
            :placeholder="$t('sentence.fill_in_code')"
            pattern="[0-9.]+"
            maxlength="6"
            v-model="pin"
          >

          <v-form-errors
            :errors="pinErrors"
          />
        </div>

        <v-button
          text="word.send"
          :block="true"
          type="submit"
          :loader="loader"
          id="register-pin-submit"
        />
      </form>
      <form method="post" @submit.prevent="createAccount" v-else>
        <div class="text-muted mb-3">
          {{ $t('sentence.register_explanation') }}
        </div>

        <div class="mb-3" v-if="asOrganisation">
          <v-text-input
            id="register-organisation-name"
            :auto-focus="true"
            :required="true"
            label="word.organisation_name"
            v-model="organisationName"
            :errors="errors.organisation_name"
          />
        </div>

        <div class="mb-3">
          <v-text-input
            id="register-email"
            label="word.email"
            type="email"
            :auto-focus="!asOrganisation"
            :required="true"
            v-model="data.email"
            :errors="errors.email"
          />
        </div>

        <div class="mb-3">
          <v-text-input
            id="register-password"
            label="word.password"
            type="password"
            v-model="data.password1"
            :errors="errors.password1"
            :required="true"
          />
        </div>
        <div class="mb-3">
          <v-text-input
            id="register-password-repeat"
            label="word.password_repeat"
            type="password"
            v-model="data.password2"
            :errors="errors.password2"
            :required="true"
          />
        </div>
        <div class="mb-3" v-if="data.total_price > 0">
          <label>
            <input
              id="register-accept-price"
              v-model="acceptPrice"
              class="mr-2"
              type="checkbox"
            />
            <span
              class="mr-2"
              v-html="priceText"
            />

            <button
              @click="showModal = true;"
              class="btn btn-sm btn-link"
              type="button"
            >
              <i class="fa fa-info-circle"></i>
            </button>
          </label>
        </div>
        <div class="mb-3">
          <label v-if="!asOrganisation" class="mb-3">
            <v-check-box
              id="register-notify-me"
              label="sentence.notify_me_short"
              help-text="sentence.notify_me"
              v-model="data.news_letter"
              :errors="errors.news_letter"
            />
          </label>
          <label>
            <input
              id="register-accept-terms"
              v-model="acceptTerms"
              class="mr-2"
              type="checkbox"
            />
            <span v-html="$t('sentence.accept_terms')"></span>
          </label>
        </div>

        <v-form-errors
          :errors="errors.non_field_errors"
        />

        <v-button
          id="register-submit"
          text="word.send"
          :block="true"
          type="submit"
          :loader="loader"
        />
      </form>
    </transition>

    <v-modal
      v-if="showModal"
      @hide-modal="hideModal"
      size="md"
    >
      <template v-slot:title>
        <h4>
          <i class="fa fa-info-circle mr-2 text-info"></i> {{ $t('phrase.register_as_agency') }}
        </h4>
      </template>

      <template v-slot:body>
        <h5>Wat zijn de voordelen?</h5>

        <em>
          Uw bureau wordt weergegeven op de
          <a href="/organisations/?type=media_agency" target="_blank">
            bureauoverizchtspagina
          </a>
          met:
        </em>
        <ul>
          <li>algemene bedrijfsinformatie;</li>
          <li>een link naar uw website;</li>
          <li>een link naar uw profielpagina.</li>
        </ul>

        <em>Uw bureau krijgt een profielpagina met daarop:</em>
        <ul>
          <li>algemene bedrijfsinformatie;</li>
          <li>een link naar uw website;</li>
          <li>een overzicht van alle door uw bureau ontwikkelde commercials die op ons
            platform zijn aangemeld.
          </li>
        </ul>
      </template>
    </v-modal>
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import CheckBox from '@/common/components/inputs/CheckBox.vue';
import FormErrors from '@/common/components/FormErrors.vue';
import Modal from '@/common/components/Modal.vue';
import TextInput from '@/common/components/inputs/TextInput.vue';
import api from '@/common/api';
import { mapActions } from 'vuex';

export default {
  name: 'RegisterForm',
  components: {
    'v-button': Button,
    'v-check-box': CheckBox,
    'v-form-errors': FormErrors,
    'v-text-input': TextInput,
    'v-modal': Modal,
  },
  props: {
    asOrganisation: {
      type: Boolean,
      default() {
        return false;
      },
    },
    organisationType: {
      type: String,
      default() {
        return 'default';
      },
    },
    smallScreen: {
      type: Boolean,
      default() {
        return false;
      },
    },
    totalPrice: {
      // Price the user agrees to be invoiced for;
      type: Number,
      default() {
        return 0;
      },
    },
  },
  data() {
    return {
      acceptPrice: false,
      acceptTerms: false,
      data: {
        email: '',
        total_price: 0,
        password1: '',
        password2: '',
        news_letter: false,
        use_pin: true,
      },
      organisationName: null,
      errors: { non_field_errors: [] },
      loader: { loading: false },
      pin: '',
      pinErrors: [],
      resendPin: false,
      showModal: false,
      usePin: false,
    };
  },
  computed: {
    anonymousEmail() {
      const emailArray = this.data.email.split('@');
      const domainSplit = emailArray[1].split('.');
      return `${emailArray[0].slice(0, 2)}*******@*****.${domainSplit[domainSplit.length - 1]}`;
    },
    priceText() {
      return this.$t(
        'sentence.accept_price_year_licence',
        { totalPrice: this.data.total_price },
      );
    },
  },
  methods: {
    ...mapActions(['refreshUser']),
    async authenticateWithPin() {
      this.pinErrors = [];
      this.loader.loading = true;
      const response = await api.post(
        '/auth/authenticate-with-pin/',
        { pin: this.pin, email: this.data.email },
      );

      if (response.status === 400) {
        this.pinErrors = [this.$t('sentence.invalid_code')];
        this.loader.loading = false;
        return;
      }

      this.$store.commit('login', response.data);
      sessionStorage.removeItem('registerEmail');
      await this.refreshUser();
      this.$emit('authenticated', response.data);
    },
    async createAccount() {
      this.errors = { non_field_errors: [] };
      if (!this.acceptTerms) {
        this.errors.non_field_errors.push(this.$t('sentence.accept_terms_required'));
        return;
      }
      this.loader.loading = true;

      if (this.asOrganisation) {
        this.data.organisation_type = this.organisationType;
        this.data.organisation_name = this.organisationName;
      }

      const response = await api.post('/auth/register/', this.data);

      if (response.status === 400) {
        this.loader.loading = false;
        this.errors = response.data;
        return;
      }
      this.loader.loading = false;
      this.usePin = true;
      sessionStorage.setItem('registerEmail', response.data.email);
    },
    hideModal() {
      this.showModal = false;
    },
    async resetPin() {
      this.loader.loading = true;
      await api.post('/auth/reset-pin/', { email: this.data.email });
      this.resendPin = true;
      this.loader.loading = false;
    },
    resetRegistration() {
      sessionStorage.removeItem('registerEmail');
      this.data = {
        email: '',
        total_price: 0,
        password1: '',
        password2: '',
        use_pin: true,
      };
      this.usePin = false;
    },
  },
  created() {
    this.data.total_price = this.totalPrice;
  },
  mounted() {
    if (sessionStorage.getItem('registerEmail')) {
      this.data.email = sessionStorage.getItem('registerEmail');
      this.usePin = true;
      setTimeout(() => {
        this.$refs.pinCode.focus();
      }, 1);
    }
  },
};
</script>

<style scoped>
  .form-control-lg {
    font-size: 30px !important;
    height: 80px;
    letter-spacing: 5px;
  }
</style>
